import { useCallback, useEffect, useMemo, useRef } from "react";
import { MediaPlayerInstance, useMediaRemote, type MediaPlayRequestEvent } from "@vidstack/react";

import type { TimelinePlayerBridge } from "../bridge";

// `<video>.currentTime` is treated as "at end" when within this much of `duration`.
const END_OF_VIDEO_EPSILON_S = 0.01;

// When `#resetPlaybackIfNeeded` dispatches a `media-seek-request` to 0, we cancel
// it unless the underlying <video> is meaningfully before end.
const REPLAY_TOLERANCE_S = 0.05;

// How much `state.realCurrentTime` may drift from `<video>.currentTime` before the
// continuous guard treats it as corruption (and restores from <video>).
const DRIFT_TOLERANCE_S = 0.05;

type Signal<T> = { set: (v: T) => void };
type PlayerInternals = MediaPlayerInstance & {
  $state?: {
    ended?: Signal<boolean>;
    realCurrentTime?: Signal<number>;
    paused?: Signal<boolean>;
    playing?: Signal<boolean>;
  };
  provider?: { video?: HTMLVideoElement; setCurrentTime?: (time: number) => void } | null;
};

function getVideoElement(player: MediaPlayerInstance | null): HTMLVideoElement | null {
  return (player as PlayerInternals | null)?.provider?.video ?? null;
}

/**
 * Creates a TimelinePlayerBridge from a Vidstack MediaPlayerInstance.
 */
export function useVidstackBridge(playerRef: React.RefObject<MediaPlayerInstance | null>): TimelinePlayerBridge {
  const remote = useMediaRemote(playerRef);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    return player.subscribe((state) => {
      const video = getVideoElement(player);
      if (!video) return;
      // Touch both signals so the effect re-runs when either changes.
      const reportedTime = state.realCurrentTime;
      const reportedEnded = state.ended;
      const drift = Math.abs(reportedTime - video.currentTime);
      const videoAtEnd =
        Number.isFinite(video.duration) && video.currentTime >= video.duration - END_OF_VIDEO_EPSILON_S;
      if (videoAtEnd) return; // genuine end — leave Vidstack alone
      if (drift > DRIFT_TOLERANCE_S || reportedEnded) {
        const $state = (player as PlayerInternals).$state;
        $state?.realCurrentTime?.set(video.currentTime);
        $state?.ended?.set(false);
      }
    });
  }, [playerRef]);

  // Block Vidstack's auto-reset-to-zero from `MediaStateManager.#resetPlaybackIfNeeded`.
  // That helper dispatches `media-seek-request` to seekableStart on any play attempt
  // when `state.ended` is true, `state.realCurrentTime` is out of range, the clipEndTime
  // has been reached, OR `Math.abs(currentTime - duration) < 0.1`. The last condition can
  // fire on legitimate scrubs near the end (e.g. seekableEnd reports tighter than the
  // real duration), and stale `ended` state can survive the seeked-handler corruption
  // chain — so this guard catches both. We let the seek-to-zero through only when the
  // underlying <video> is actually at end (genuine replay).
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    const playerEl = (player as { el?: EventTarget | null }).el;
    const targetsThisPlayer = (event: Event) => !playerEl || event.composedPath().includes(playerEl);

    const onSeekRequest = (event: Event) => {
      if (!targetsThisPlayer(event)) return;
      const detail = (event as CustomEvent<number>).detail;
      if (detail !== 0) return;
      const video = getVideoElement(player);
      if (!video) return;
      const realDuration = video.duration;
      if (!Number.isFinite(realDuration)) return;
      if (video.currentTime < realDuration - REPLAY_TOLERANCE_S) {
        event.preventDefault();
      }
    };

    const onPlayRequest = (event: Event) => {
      if (!targetsThisPlayer(event)) return;
      const video = getVideoElement(player);
      const trigger = (event as MediaPlayRequestEvent).trigger;
      if (video?.paused && trigger?.type === "seeked" && trigger.target === video) {
        // HTMLMediaEvents treats differing duration/seek decimal precision as
        // an unfinished end seek and requests play. A seek must preserve pause;
        // explicit controls/replay have a different trigger and still pass.
        // This library-generated request is not cancelable, so intercept it
        // before Vidstack's player listener instead of only preventing default.
        event.stopImmediatePropagation();
        (player as PlayerInternals).$state?.realCurrentTime?.set(video.currentTime);
      }
    };

    document.addEventListener("media-seek-request", onSeekRequest, { capture: true });
    document.addEventListener("media-play-request", onPlayRequest, { capture: true });
    return () => {
      document.removeEventListener("media-seek-request", onSeekRequest, { capture: true });
      document.removeEventListener("media-play-request", onPlayRequest, { capture: true });
    };
  }, [playerRef]);

  const getCurrentTime = useCallback(() => {
    return getVideoElement(playerRef.current)?.currentTime ?? 0;
  }, [playerRef]);

  const getDuration = useCallback(() => getVideoElement(playerRef.current)?.duration ?? NaN, [playerRef]);

  // Shared rAF loop for subscribeToTime: one loop per bridge instance fans out
  // to all subscribers. A typical VideoPlayer mounts ~5 time consumers
  // (TimeDisplay + 3× TimelineCanvas + AnnotationsOverlay), so per-subscriber
  // loops would all be polling the same `<video>.currentTime`. Shared loop is
  // also the natural place to add gating (e.g. `<video>.seeking`) if needed.
  const timeSubsRef = useRef<Set<(time: number) => void>>(new Set());
  const rafIdRef = useRef(0);
  const lastTimeRef = useRef(-1);

  // Cancel the loop on unmount.
  useEffect(() => {
    const subscribers = timeSubsRef.current;
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = 0;
      }
      subscribers.clear();
    };
  }, []);

  const subscribeToTime = useCallback(
    (callback: (time: number) => void) => {
      const subs = timeSubsRef.current;
      const wasEmpty = subs.size === 0;
      subs.add(callback);

      // Fire immediately with current time so new subscribers receive an
      // initial value without waiting for the next playback frame. Matches the
      // semantic of the previous per-subscriber rAF (which fired on its first
      // tick because `lastTime` started at `-1`).
      const video = getVideoElement(playerRef.current);
      if (video) callback(video.currentTime);

      if (wasEmpty) {
        lastTimeRef.current = video?.currentTime ?? -1;
        const tick = () => {
          if (timeSubsRef.current.size === 0) {
            rafIdRef.current = 0;
            return;
          }
          const v = getVideoElement(playerRef.current);
          if (v) {
            const time = v.currentTime;
            if (time !== lastTimeRef.current) {
              lastTimeRef.current = time;
              timeSubsRef.current.forEach((cb) => cb(time));
            }
          }
          rafIdRef.current = requestAnimationFrame(tick);
        };
        rafIdRef.current = requestAnimationFrame(tick);
      }

      return () => {
        subs.delete(callback);
        if (subs.size === 0 && rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = 0;
        }
      };
    },
    [playerRef],
  );

  const isPaused = useCallback(() => {
    return getVideoElement(playerRef.current)?.paused ?? true;
  }, [playerRef]);

  const subscribeToPaused = useCallback(
    (callback: (paused: boolean) => void) => {
      const player = playerRef.current;
      if (!player) return () => {};
      let last: boolean | null = null;
      return player.subscribe((state) => {
        if (state.paused === last) return;
        last = state.paused;
        callback(state.paused);
      });
    },
    [playerRef],
  );

  const isEnded = useCallback(() => {
    return getVideoElement(playerRef.current)?.ended ?? false;
  }, [playerRef]);

  const seek = useCallback(
    (time: number) => {
      const player = playerRef.current;
      const provider = (player as PlayerInternals | null)?.provider;
      if (provider?.setCurrentTime) {
        provider.setCurrentTime(time);
      } else {
        remote.seek(time);
      }
    },
    [playerRef, remote],
  );

  // A drag that seeks every frame aborts each seek before it decodes, so the
  // picture freezes until the pointer stops. Keep one seek in flight and only
  // the latest request behind it, so every seek lands and the frame follows.
  const pendingScrubRef = useRef<number | null>(null);
  const flushScrub = useCallback(() => {
    const pending = pendingScrubRef.current;
    pendingScrubRef.current = null;
    if (pending !== null) seek(pending);
  }, [seek]);
  const scrubSeek = useCallback(
    (time: number | null) => {
      if (time === null) {
        // The release position is authoritative: apply it without waiting.
        flushScrub();
        return;
      }
      const video = getVideoElement(playerRef.current);
      if (video?.seeking) {
        if (pendingScrubRef.current === null) video.addEventListener("seeked", flushScrub, { once: true });
        pendingScrubRef.current = time;
        return;
      }
      // This seek supersedes anything still pending, so a late `seeked` can't replay an older target.
      pendingScrubRef.current = null;
      seek(time);
    },
    [flushScrub, playerRef, seek],
  );

  const play = useCallback(() => {
    remote.play();
  }, [remote]);

  const pause = useCallback(() => {
    remote.pause();
    const player = playerRef.current;
    if (getVideoElement(player)?.paused) {
      // Native pause is synchronous, but its event is queued. A frame seek can
      // lower readyState to HAVE_METADATA before that event arrives, which
      // Vidstack ignores. Keep its controls/play gate aligned with the actual
      // pause instead of leaving a paused video stuck in the playing state.
      const $state = (player as PlayerInternals).$state;
      $state?.playing?.set(false);
      $state?.paused?.set(true);
    }
  }, [playerRef, remote]);

  const toggleMuted = useCallback(() => remote.toggleMuted(), [remote]);
  const toggleFullscreen = useCallback(() => remote.toggleFullscreen("media"), [remote]);

  return useMemo(
    () => ({
      getCurrentTime,
      getDuration,
      subscribeToTime,
      isPaused,
      subscribeToPaused,
      isEnded,
      seek,
      scrubSeek,
      play,
      pause,
      toggleMuted,
      toggleFullscreen,
    }),
    [
      getCurrentTime,
      getDuration,
      subscribeToTime,
      isPaused,
      subscribeToPaused,
      isEnded,
      seek,
      scrubSeek,
      play,
      pause,
      toggleMuted,
      toggleFullscreen,
    ],
  );
}
